import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';
const base = `${API}/api/food-safety`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// ─── Plans ─────────────────────────────────────────────────────────────────────
const getPlans         = ()           => axios.get(`${base}/plans`, auth()).then(r => r.data.plans);
const createPlan       = (data)       => axios.post(`${base}/plans`, data, auth()).then(r => r.data);
const getPlan          = (planType)   => axios.get(`${base}/plans/${planType}`, auth()).then(r => r.data);
const updatePlan       = (planType, data) => axios.patch(`${base}/plans/${planType}`, data, auth()).then(r => r.data.plan);

// ─── Sections ─────────────────────────────────────────────────────────────────
const saveSection      = (planType, sectionKey, content) =>
  axios.put(`${base}/plans/${planType}/sections/${sectionKey}`, { content }, auth()).then(r => r.data.section);
const linkSection      = (planType, sectionKey, linkedPolicyId) =>
  axios.put(`${base}/plans/${planType}/sections/${sectionKey}`, { linkedPolicyId }, auth()).then(r => r.data.section);
const unlinkSection    = (planType, sectionKey) =>
  axios.put(`${base}/plans/${planType}/sections/${sectionKey}`, { unlink: true }, auth()).then(r => r.data.section);

// ─── Section definitions (keys, titles, contentTypes) ─────────────────────────
const getSectionDefs   = ()           => axios.get(`${base}/section-defs`, auth()).then(r => r.data);

// ─── Shared Library ────────────────────────────────────────────────────────────
const getLibrary          = ()                    => axios.get(`${base}/library`, auth()).then(r => r.data.sections);
const saveLibrarySection  = (sectionKey, content) => axios.put(`${base}/library/${sectionKey}`, { content }, auth()).then(r => r.data.section);
const linkLibrarySection  = (sectionKey, linkedPolicyId) => axios.put(`${base}/library/${sectionKey}`, { linkedPolicyId }, auth()).then(r => r.data.section);
const unlinkLibrarySection= (sectionKey)          => axios.put(`${base}/library/${sectionKey}`, { unlink: true }, auth()).then(r => r.data.section);

// ─── Products ─────────────────────────────────────────────────────────────────
const getProducts      = (planType)   => axios.get(`${base}/plans/${planType}/products`, auth()).then(r => r.data.products);
const addProduct       = (planType, data) => axios.post(`${base}/plans/${planType}/products`, data, auth()).then(r => r.data.planProduct);
const updateProduct    = (planType, id, data) => axios.put(`${base}/plans/${planType}/products/${id}`, data, auth()).then(r => r.data.planProduct);
const removeProduct    = (planType, id) => axios.delete(`${base}/plans/${planType}/products/${id}`, auth()).then(r => r.data);

// ─── Versions ─────────────────────────────────────────────────────────────────
const getVersions      = (planType)   => axios.get(`${base}/plans/${planType}/versions`, auth()).then(r => r.data.versions);
const approvePlan      = (planType, data) => axios.post(`${base}/plans/${planType}/approve`, data, auth()).then(r => r.data);

// ─── Share Links ───────────────────────────────────────────────────────────────
const getShareLinks    = (planType)   => axios.get(`${base}/plans/${planType}/share`, auth()).then(r => r.data.links);
const createShareLink  = (planType, data) => axios.post(`${base}/plans/${planType}/share`, data, auth()).then(r => r.data.link);
const revokeShareLink  = (token)      => axios.delete(`${base}/share/${token}`, auth()).then(r => r.data);

// ─── Snapshot (authenticated print/preview) ───────────────────────────────────
const getPlanSnapshot  = (planType)   => axios.get(`${base}/plans/${planType}/snapshot`, auth()).then(r => r.data);

// ─── Public reader (no auth) ──────────────────────────────────────────────────
const getPublicPlan    = (token)      => axios.get(`${base}/public/${token}`).then(r => r.data);

// ─── SSE stream helper ────────────────────────────────────────────────────────
function sseStream(url, body, onProgress) {
  return new Promise(async (resolve, reject) => {
    let response;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(body),
      });
    } catch (e) { return reject(e); }
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Request failed' }));
      return reject(err);
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split('\n\n');
        buf = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: /, '').trim();
          if (!line || line.startsWith(':')) continue;
          try {
            const ev = JSON.parse(line);
            if (ev.type === 'progress') onProgress?.(ev.message);
            else if (ev.type === 'done')  return resolve(ev);
            else if (ev.type === 'error') return reject(new Error(ev.message));
          } catch {}
        }
      }
    } catch (e) { reject(e); }
  });
}

const importPlan        = (planType, body, onProgress) =>
  sseStream(`${base}/plans/${planType}/ai/import`, body, onProgress).then(ev => ev.sections);
const generateSection   = (planType, sectionKey, body, onProgress) =>
  sseStream(`${base}/plans/${planType}/ai/generate-section`, { sectionKey, ...body }, onProgress).then(ev => ev.content);
const checkProductRules = (planType, ppId, { allergens, ingredients } = {}) =>
  axios.post(`${base}/plans/${planType}/products/${ppId}/ai/check-rules`, { allergens, ingredients }, auth()).then(r => r.data.rules);

const foodSafetyService = {
  getPlans, createPlan, getPlan, updatePlan,
  saveSection, linkSection, unlinkSection, getSectionDefs,
  getLibrary, saveLibrarySection, linkLibrarySection, unlinkLibrarySection,
  getProducts, addProduct, updateProduct, removeProduct,
  getVersions, approvePlan,
  getShareLinks, createShareLink, revokeShareLink,
  getPlanSnapshot, getPublicPlan,
  importPlan, generateSection, checkProductRules,
};

export default foodSafetyService;
